'use client';

import { Box, Typography } from '@mui/material';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import type { BuzzBridgeRequestRow } from '../utils/mapBuzzBridgeRequest';
import { CampaignThreadUnreadBadge } from '../CampaignThreadUnreadBadge';

const SURFACE_CARD = '#1a1919';
const SURFACE_HI = '#262626';
const GOLD = '#ffc965';
const ON_VARIANT = '#adaaaa';
const PENDING_CREAM_BG = 'rgba(254, 249, 195, 0.14)';
const PENDING_CREAM_TEXT = '#fef3c7';
const RADIUS_FULL = '0.75rem';

export interface RequestsListProps {
    requests: BuzzBridgeRequestRow[];
    onCampaignClick?: (campaignId: string, participantId?: string) => void;
}

export function RequestsList({ requests, onCampaignClick }: RequestsListProps) {
    const metaIconSx = { fontSize: 18, color: ON_VARIANT };

    return (
        <Box component="section" sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%', minWidth: 0 }}>
            {requests.length === 0 ? (
                <Box
                    sx={{
                        bgcolor: SURFACE_CARD,
                        borderRadius: 2,
                        p: 5,
                        textAlign: 'center',
                        border: '1px solid rgba(255,255,255,0.04)',
                    }}>
                    <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 14, color: ON_VARIANT }}>
                        No requests found
                    </Typography>
                </Box>
            ) : (
                requests.map(req => {
                    const pending = req.status === 'pending';
                    const accepted = req.status === 'accepted';
                    const rejected = req.status === 'rejected';
                    const canOpen = Boolean(req.campaignId && onCampaignClick && !pending);
                    const open = () => onCampaignClick?.(req.campaignId, req.participantId || undefined);

                    const statusChip = accepted
                        ? {
                              label: 'Accepted',
                              icon: <CheckCircleOutlineIcon sx={{ fontSize: 16 }} />,
                              bg: 'rgba(0, 253, 193, 0.12)',
                              color: '#00fdc1',
                          }
                        : rejected
                        ? {
                              label: 'Rejected',
                              icon: <CancelOutlinedIcon sx={{ fontSize: 16 }} />,
                              bg: 'rgba(255, 115, 81, 0.12)',
                              color: '#ff7351',
                          }
                        : {
                              label: 'Pending',
                              icon: <HourglassEmptyIcon sx={{ fontSize: 16 }} />,
                              bg: PENDING_CREAM_BG,
                              color: PENDING_CREAM_TEXT,
                          };

                    return (
                        <Box
                            key={req.id}
                            onClick={() => canOpen && open()}
                            onKeyDown={e => {
                                if (canOpen && (e.key === 'Enter' || e.key === ' ')) {
                                    e.preventDefault();
                                    open();
                                }
                            }}
                            role={canOpen ? 'button' : undefined}
                            tabIndex={canOpen ? 0 : undefined}
                            sx={{
                                bgcolor: SURFACE_CARD,
                                borderRadius: 2,
                                p: 2.5,
                                border: '1px solid rgba(255,255,255,0.04)',
                                transition: 'background-color 0.2s',
                                cursor: canOpen ? 'pointer' : 'default',
                                '&:hover': canOpen ? { bgcolor: SURFACE_HI } : {},
                            }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'flex-start',
                                    gap: 2,
                                    mb: 2,
                                }}>
                                <Box
                                    sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 0.75,
                                        px: 1.5,
                                        py: 0.5,
                                        borderRadius: RADIUS_FULL,
                                        bgcolor: statusChip.bg,
                                        color: statusChip.color,
                                        fontSize: 12,
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        fontWeight: 700,
                                    }}>
                                    {statusChip.icon}
                                    {statusChip.label}
                                </Box>
                                {pending ? (
                                    <Box
                                        sx={{
                                            px: 1.75,
                                            py: 0.5,
                                            borderRadius: RADIUS_FULL,
                                            border: `1px solid ${GOLD}`,
                                            color: GOLD,
                                            fontSize: 11,
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            fontWeight: 700,
                                            letterSpacing: '0.06em',
                                            textTransform: 'uppercase',
                                            flexShrink: 0,
                                        }}>
                                        Awaiting Response
                                    </Box>
                                ) : accepted ? (
                                    <Box
                                        sx={{
                                            px: 1.75,
                                            py: 0.5,
                                            borderRadius: RADIUS_FULL,
                                            border: `1px solid rgba(0,253,193,0.45)`,
                                            color: '#00fdc1',
                                            fontSize: 11,
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            fontWeight: 700,
                                            letterSpacing: '0.06em',
                                            textTransform: 'uppercase',
                                        }}>
                                        Approved
                                    </Box>
                                ) : (
                                    <Box
                                        sx={{
                                            px: 1.75,
                                            py: 0.5,
                                            borderRadius: RADIUS_FULL,
                                            border: `1px solid rgba(255,115,81,0.45)`,
                                            color: '#ff7351',
                                            fontSize: 11,
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            fontWeight: 700,
                                            letterSpacing: '0.06em',
                                            textTransform: 'uppercase',
                                        }}>
                                        Closed
                                    </Box>
                                )}
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                <Typography
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        fontWeight: 700,
                                        fontSize: 17,
                                        color: '#fff',
                                        lineHeight: 1.3,
                                    }}>
                                    {req.title}
                                </Typography>
                                <CampaignThreadUnreadBadge count={req.unreadMessageCount} size="sm" />
                            </Box>
                            {req.company ? (
                                <Typography
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        fontSize: 13,
                                        color: ON_VARIANT,
                                        mb: req.description ? 1 : 2,
                                    }}>
                                    {req.company}
                                </Typography>
                            ) : null}
                            {req.description ? (
                                <Typography
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        fontSize: 13,
                                        color: ON_VARIANT,
                                        mb: 2,
                                        display: '-webkit-box',
                                        WebkitLineClamp: 3,
                                        WebkitBoxOrient: 'vertical',
                                        overflow: 'hidden',
                                        lineHeight: 1.5,
                                    }}>
                                    {req.description}
                                </Typography>
                            ) : null}

                            <Box
                                sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    alignItems: 'center',
                                    columnGap: 2.5,
                                    rowGap: 1,
                                }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                    <AttachMoneyIcon sx={metaIconSx} />
                                    <Typography
                                        sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 13, color: ON_VARIANT }}>
                                        {req.budget}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                    <CalendarTodayIcon sx={metaIconSx} />
                                    <Typography
                                        sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 13, color: ON_VARIANT }}>
                                        {req.deadline}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                    <AccessTimeIcon sx={metaIconSx} />
                                    <Typography
                                        sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 13, color: ON_VARIANT }}>
                                        {req.appliedDate !== '—' ? `Applied ${req.appliedDate}` : 'Applied'}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    );
                })
            )}
        </Box>
    );
}
